import { useRef } from 'react';
import { Redirect, useParams } from 'react-router-dom';

import { browserHistory } from '/app/history';
import type { Route, RouteParams } from '/module/routing/types';
import { setCurrentSiteId } from '/module/sites';
import { useCurrentSiteId } from '/module/sites/hooks';
import { siteTokenToId } from '/module/sites/util';

/**
 * Get full url path: pathname including query params
 */
const getFullPath = (pathname = window.location.pathname, search = window.location.search) => {
  return [
    // Prevent trailing slash
    pathname.replace(/\/+$/g, ''),
    // Prevent empty search
    search === '?' ? '' : window.location.search,
  ]
    .filter(Boolean)
    .join('');
};

/**
 * Checks if query params includes site id and checks if this id
 * matches the current site id.
 *
 * If it doesn't match, the current site is updated to match the
 * id in the params.
 */
export function SitePreflight({ route, children }: { route: Route; children: React.ReactElement }) {
  // Keep track of first render (page load)
  const firstRender = useRef(true);

  const params = useParams<RouteParams>();

  // If the site id is actually a legacy site token
  if (params.siteId && params.siteId.length > 8 && params.siteId.includes('-')) {
    // Convert legacy token to 8-char site id
    const siteToken = params.siteId;
    const siteId = siteTokenToId(siteToken);

    if (siteId) {
      // Update URL including query params
      browserHistory.replace(getFullPath(window.location.pathname.replace(siteToken, siteId)));
      params.siteId = siteId;
    } else {
      // If conversion failed somehow, clear the site id
      params.siteId = undefined;
    }
  }

  const currentSiteId = useCurrentSiteId();
  if (!currentSiteId) {
    // Nothing to handle if we don't have a current site id
    return children;
  }

  if (firstRender.current) {
    // ℹ️ Current site id must follow URL
    //
    // If the user opens the dashboard with a specific site id, we automatically
    // set that as the current site.

    // If the current route has a :siteId parameter, but no site id is present in the url
    const pathWithSiteId = route.path?.find((p) => p.startsWith('/p') && p.includes(':siteId'));
    if (typeof params.siteId === 'undefined' && pathWithSiteId) {
      // Replace :siteId param with the current site id
      let pathWithParams = pathWithSiteId.replace(':siteId', currentSiteId);

      // Replace any param that may be required in for the alternate path.
      for (const [paramName, value] of Object.entries(params)) {
        if (value) {
          pathWithParams = pathWithParams.replace(`:${paramName}`, value);
        }
      }

      // Redirect to the path with the current site id
      return <Redirect to={pathWithParams} />;
    }

    // If there's a site id in the param, and it doesn't match the current site id
    if (params.siteId && params.siteId !== currentSiteId) {
      if (import.meta.env.APP_NODE_ENV === 'development') {
        console.debug(
          `Param for site id (${params.siteId}) does not match current site id (${currentSiteId})`,
          'Automatically switching site...',
        );
      }

      // Set new current site based on the siteId in the param.
      // If the siteId in the URL is invalid, we'll revert to the default site.
      const setSiteId = setCurrentSiteId(params.siteId, true);
      if (setSiteId) {
        // Update URL
        browserHistory.replace(getFullPath(window.location.pathname.replace(params.siteId, setSiteId)));
      }
    }

    firstRender.current = false;
  } else {
    // ℹ️ URL must follow current site id
    //
    // If the user changes the current site, the URL will update to match it.

    let newLoc: string | undefined;
    if (!params.siteId) {
      // If there's no site id in the param
      newLoc = getFullPath(window.location.pathname.replace(/\/(p\/)?/gi, `/p/${currentSiteId}/`));
    } else if (params.siteId !== currentSiteId) {
      // If there's a site id in the param, and it doesn't match the current site id
      newLoc = getFullPath(window.location.pathname.replace(params.siteId, currentSiteId));
    }

    if (newLoc) {
      if (import.meta.env.APP_NODE_ENV === 'development') {
        console.debug(`Updating URL for new site id (${currentSiteId}): ${newLoc}`);
      }

      browserHistory.replace(newLoc);
    }
  }

  return children;
}
